import { eventTracker } from "app/modules/Segment";
import {
  FeatureEnum,
  GroupIdEnum,
  OwnerEnum,
  TContext,
  TProperties,
} from "app/modules/Segment/types";
import { snakeCaseToTitleCase } from "app/modules/Segment/utils";

export const PASSWORD_EVENTS = {
  PASSWORD_CREATION_PAGE_DISPLAYED: "password_creation_page_displayed",
  PASSWORD_CREATION_ATTEMPT_STARTED: "password_creation_attempt_started",
  PASSWORD_CREATION_SUCCESSFUL: "password_creation_successful",
  PASSWORD_RESET_EMAIL_SENT: "password_reset_email_sent",
  PASSWORD_RESET_PAGE_DISPLAYED: "password_reset_page_displayed",
  PASSWORD_RESET_ATTEMPT_STARTED: "password_reset_attempt_started",
  PASSWORD_RESET_SUCCESSFUL: "password_reset_successful",
  PASSWORD_CREATION_MODAL_DISPLAYED: "password_creation_modal_displayed",
  PASSWORD_CREATION_MODAL_DISMISSED: "password_creation_modal_dismissed",
  PASSWORD_CREATION_MODAL_ATTEMPT_STARTED:
    "password_creation_modal_attempt_started",
  PASSWORD_CREATION_MODAL_SUCCESSFUL: "password_creation_modal_successful",
} as const;

const PASSWORD_LIBRARY_NAME = "general-library";
const PASSWORD_CATEGORY = "authentication";

function buildBaseProperties(
  event: string,
  eventMeaning: string,
  relevantUserActivity: boolean,
  extraProperties: Partial<TProperties> = {}
): TProperties {
  return {
    ...extraProperties,
    owner: OwnerEnum.PLATAFORMA,
    feature: FeatureEnum.SIGNIN,
    category: PASSWORD_CATEGORY,
    event_meaning: eventMeaning,
    relevant_user_activity: relevantUserActivity,
    event_text: snakeCaseToTitleCase(event),
  } as TProperties;
}

function buildPasswordContext(component: string): TContext {
  return {
    groupId: GroupIdEnum.GENERAL_GROUP,
    library: {
      name: PASSWORD_LIBRARY_NAME,
      componente: component,
    },
  };
}

export function trackPasswordCreationPageDisplayed(): void {
  const event = PASSWORD_EVENTS.PASSWORD_CREATION_PAGE_DISPLAYED;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando a tela de criação de senha do primeiro acesso é exibida.",
      false
    ),
    context: buildPasswordContext("input-component"),
  });
}

export function trackPasswordCreationAttemptStarted(): void {
  const event = PASSWORD_EVENTS.PASSWORD_CREATION_ATTEMPT_STARTED;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando o usuário envia o formulário de criação de senha no primeiro acesso.",
      false
    ),
    context: buildPasswordContext("button-component"),
  });
}

export function trackPasswordCreationSuccessful(): void {
  const event = PASSWORD_EVENTS.PASSWORD_CREATION_SUCCESSFUL;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado após a criação de senha no primeiro acesso ser concluída com sucesso.",
      true
    ),
    context: buildPasswordContext("modal-component"),
  });
}

export function trackPasswordResetEmailSent(): void {
  const event = PASSWORD_EVENTS.PASSWORD_RESET_EMAIL_SENT;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando a solicitação de redefinição de senha é enviada com sucesso.",
      false
    ),
    context: buildPasswordContext("input-component"),
  });
}

export function trackPasswordResetPageDisplayed(): void {
  const event = PASSWORD_EVENTS.PASSWORD_RESET_PAGE_DISPLAYED;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando a tela de redefinição de senha com token é exibida.",
      false
    ),
    context: buildPasswordContext("input-component"),
  });
}

export function trackPasswordResetAttemptStarted(): void {
  const event = PASSWORD_EVENTS.PASSWORD_RESET_ATTEMPT_STARTED;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando o usuário envia o formulário de redefinição de senha.",
      false
    ),
    context: buildPasswordContext("button-component"),
  });
}

export function trackPasswordResetSuccessful(): void {
  const event = PASSWORD_EVENTS.PASSWORD_RESET_SUCCESSFUL;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado após a redefinição de senha ser concluída com sucesso.",
      true
    ),
    context: buildPasswordContext("modal-component"),
  });
}

export function trackPasswordCreationModalDisplayed(): void {
  const event = PASSWORD_EVENTS.PASSWORD_CREATION_MODAL_DISPLAYED;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando o modal de criação de senha é exibido no dashboard.",
      false
    ),
    context: buildPasswordContext("modal-component"),
  });
}

export function trackPasswordCreationModalDismissed(): void {
  const event = PASSWORD_EVENTS.PASSWORD_CREATION_MODAL_DISMISSED;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando o usuário fecha o modal de criação de senha sem criar a senha.",
      false
    ),
    context: buildPasswordContext("modal-component"),
  });
}

export function trackPasswordCreationModalAttemptStarted(): void {
  const event = PASSWORD_EVENTS.PASSWORD_CREATION_MODAL_ATTEMPT_STARTED;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando o usuário envia o formulário do modal de criação de senha.",
      false
    ),
    context: buildPasswordContext("button-component"),
  });
}

export function trackPasswordCreationModalSuccessful(): void {
  const event = PASSWORD_EVENTS.PASSWORD_CREATION_MODAL_SUCCESSFUL;

  eventTracker.track({
    event,
    properties: buildBaseProperties(
      event,
      "Disparado quando a senha é criada pelo modal do dashboard.",
      true
    ),
    context: buildPasswordContext("modal-component"),
  });
}
